// Contact.jsx - Página de contacto con formulario y WhatsApp
import React, { useState, useEffect } from 'react';
import { 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaWhatsapp, 
  FaPaw, 
  FaUser,
  FaPaperPlane,
  FaHeart,
  FaCheckCircle,
  FaSpinner
} from 'react-icons/fa';
import api from '../../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // API state
  const [contactInfo, setContactInfo] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  const EMAILJS_SERVICE_ID = 'service_bc5tmjb';
  const EMAILJS_TEMPLATE_ID = 'template_18cb0rk';
  const EMAILJS_PUBLIC_KEY = 'CGNNs2TkNcVYNVdI_';

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await api.getContent();
        if (response?.success && response.data?.contact?.info) {
          setContactInfo(response.data.contact.info);
        }
      } catch (error) {
        console.error('Error cargando contenido de contacto:', error);
      }
    };

    const fetchTeam = async () => {
      try {
        const response = await api.getTeam();
        if (response?.success && Array.isArray(response.data)) {
          setTeamMembers(response.data);
        }
      } catch (error) {
        console.error('Error cargando equipo:', error);
      }
    };

    fetchContent();
    fetchTeam();
  }, []);

  // Helpers con fallback
  const getEmail = () => contactInfo?.extra_data?.email || 'lchajin3@gmail.com';
  const getPhone = () => contactInfo?.extra_data?.phone || '+57 312 696 8868';
  const getAddress = () => contactInfo?.extra_data?.address || 'Barranquilla, Colombia';
  const getTitle = () => contactInfo?.title || 'Contáctanos';
  const getSubtitle = () => contactInfo?.subtitle || '¿Tienes preguntas sobre nuestro laboratorio? ¿Quieres que tu hijo forme parte de esta experiencia mágica? ¡Estamos aquí para ayudarte!';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const emailjs = await import('@emailjs/browser');
      
      const templateParams = {
        to_name: 'Equipo FeelLab',
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        reply_to: formData.email
      };

      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);

      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error enviando email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const phone = getPhone().replace(/\D/g, '');
    const message = encodeURIComponent('¡Hola! Me interesa conocer más sobre el Laboratorio de Empatía Animal FeelLab. ¿Podrían brindarme más información?');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/80 rounded-full px-6 py-3 backdrop-blur-sm mb-6">
            <FaPaw className="text-emerald-500 text-xl" />
            <span className="font-medium text-gray-700">Conectemos</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-titulo font-bold text-gray-800 mb-4">
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
              {getTitle()}
            </span>
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {getSubtitle()}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Formulario de contacto */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100">
            <div className="flex items-center space-x-3 mb-6">
              <FaEnvelope className="text-emerald-500 text-xl" />
              <h2 className="text-2xl font-titulo font-bold text-gray-800">Envíanos un mensaje</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Nombre completo *</label>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-white/80 border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors duration-300"
                      placeholder="Tu nombre"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Correo electrónico *</label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-white/80 border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors duration-300"
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Teléfono</label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-white/80 border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors duration-300"
                      placeholder="3123456789"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Asunto *</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/80 border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors duration-300"
                  >
                    <option value="">Selecciona un tema</option>
                    <option value="Información general">Información general</option>
                    <option value="Inscripción">Inscripción de mi hijo/a</option>
                    <option value="Horarios y precios">Horarios y precios</option>
                    <option value="Colaboración">Oportunidades de colaboración</option>
                    <option value="Otros">Otros</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Mensaje *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 bg-white/80 border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors duration-300 resize-none"
                  placeholder="Cuéntanos cómo podemos ayudarte..."
                ></textarea>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl flex items-center space-x-2">
                  <FaCheckCircle />
                  <span>¡Mensaje enviado exitosamente! Nos pondremos en contacto contigo pronto.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl">
                  Hubo un error al enviar el mensaje. Por favor, intenta de nuevo o contáctanos por WhatsApp.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 rounded-xl font-medium text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSubmitting ? (
                  <><FaSpinner className="animate-spin" /><span>Enviando...</span></>
                ) : (
                  <><FaPaperPlane /><span>Enviar mensaje</span></>
                )}
              </button>
            </form>
          </div>

          {/* Información de contacto y WhatsApp */}
          <div className="space-y-8">
            {/* WhatsApp destacado */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl p-8 text-white shadow-xl">
              <div className="flex items-center space-x-3 mb-6">
                <FaWhatsapp className="text-3xl" />
                <h2 className="text-2xl font-titulo font-bold">¿Prefieres hablar directamente?</h2>
              </div>
              
              <p className="text-white/90 mb-6 text-lg">
                Chatea con nosotros por WhatsApp y recibe respuesta inmediata a todas tus preguntas.
              </p>
              
              <button
                onClick={openWhatsApp}
                className="bg-white text-green-600 px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-3"
              >
                <FaWhatsapp className="text-xl" />
                <span>Chatear por WhatsApp</span>
              </button>
              
              <p className="text-white/80 text-sm mt-4">
                📱 Número: {getPhone()}
              </p>
            </div>

            {/* Información de contacto */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100">
              <h3 className="text-2xl font-titulo font-bold text-gray-800 mb-6 flex items-center space-x-2">
                <FaHeart className="text-pink-500" />
                <span>Información de contacto</span>
              </h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <FaEnvelope className="text-emerald-500 text-xl mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Correo electrónico</h4>
                    <p className="text-gray-600">{getEmail()}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <FaPhone className="text-emerald-500 text-xl mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Teléfono</h4>
                    <p className="text-gray-600">{getPhone()}</p>
                    <p className="text-gray-500 text-sm">Lunes a viernes, 8:00 AM - 6:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <FaMapMarkerAlt className="text-emerald-500 text-xl mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Ubicación</h4>
                    <p className="text-gray-600">{getAddress()}</p>
                    <p className="text-gray-500 text-sm">Sesiones virtuales y presenciales disponibles</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Información adicional */}
            <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-3xl p-8 border border-emerald-200">
              <h3 className="text-xl font-titulo font-bold text-emerald-800 mb-4">
                ¿Sabías que...?
              </h3>
              <div className="space-y-3 text-emerald-700">
                <p className="flex items-start space-x-2">
                  <span className="text-lg">🐢</span>
                  <span>Ofrecemos sesiones de prueba gratuitas para conocer nuestra metodología</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="text-lg">🎨</span>
                  <span>Adaptamos las actividades según la edad y necesidades de cada niño</span>
                </p>
                <p className="flex items-start space-x-2">
                  <span className="text-lg">🌿</span>
                  <span>También ofrecemos talleres para padres y educadores</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de equipo */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-titulo font-bold text-gray-800 mb-8">Nuestro Equipo</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {teamMembers.length > 0 ? (
              teamMembers.map((member) => (
                <div key={member.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-emerald-100">
                  <h4 className="font-bold text-gray-800 mb-2">{member.name}</h4>
                  <p className="text-emerald-600 font-medium">{member.role}</p>
                </div>
              ))
            ) : (
              <>
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-emerald-100">
                  <h4 className="font-bold text-gray-800 mb-2">Leydi Diana Saltarín Álvarez</h4>
                  <p className="text-emerald-600 font-medium"></p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-emerald-100">
                  <h4 className="font-bold text-gray-800 mb-2">Leidylizeth Hernandez Chajin</h4>
                  <p className="text-emerald-600 font-medium"></p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-emerald-100">
                  <h4 className="font-bold text-gray-800 mb-2">DR. Freddy Marín González</h4>
                  <p className="text-emerald-600 font-medium"></p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
